import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, KeyRound, Plus, Smartphone, Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import { STORE_DISPLAY_NAME } from '../../constants/branding';
import InstallPrompt from '../../components/InstallPrompt';
import LoginNotificationPrompt from '../../components/LoginNotificationPrompt';
import PasswordInput from '../../components/PasswordInput';
import { syncPushSubscriptionForCustomer } from '../../utils/pushNotifications';
import {
  completeCustomerLoginMigrationPasswordSetup,
  resolveCustomerLoginMigration,
} from '../../utils/login/migration';
import { saveCustomerLoginPhone } from '../../utils/login/savedPhone';
import { saveCustomerAuthToken } from '../../utils/auth/customerAuthToken';

const channelLabel = (channel?: string | null) => {
  switch (channel) {
    case 'telegram':
      return 'Telegram';
    case 'in_store':
      return 'In Store';
    case 'staff_assisted':
      return 'Staff Assisted';
    default:
      return 'Migration';
  }
};

const LoginMigrationPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const [channel, setChannel] = useState<string | null>(null);
  const [minPasswordLength, setMinPasswordLength] = useState(8);
  const [phoneNumbers, setPhoneNumbers] = useState(['']);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    if (!token) {
      setError('This migration link is invalid.');
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    void (async () => {
      const result = await resolveCustomerLoginMigration(token);
      if (!alive) return;

      if (!result.ok) {
        setError(result.message);
        setLoading(false);
        return;
      }

      setCustomerName(result.data.customer_name);
      setChannel(result.data.channel);
      setMinPasswordLength(result.data.min_password_length || 8);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  const finalizeLogin = async (
    customerId: number,
    name: string,
    phoneNumber: string,
    authToken: string,
  ) => {
    saveCustomerAuthToken(authToken);
    localStorage.setItem('customerId', String(customerId));
    localStorage.setItem('customerName', name);
    saveCustomerLoginPhone(phoneNumber);
    void syncPushSubscriptionForCustomer(customerId).catch((syncError) => {
      console.error('Failed to sync push subscription after migration login:', syncError);
    });

    toast.success(`Welcome, ${name}!`);
    navigate('/reserve');
  };

  const handleCompleteSetup = async () => {
    const normalizedPhoneNumbers = phoneNumbers
      .map((phoneNumber) => phoneNumber.trim())
      .filter(Boolean);

    if (normalizedPhoneNumbers.length === 0) {
      toast.warning('Please enter at least one phone number you currently use.');
      return;
    }

    if (new Set(normalizedPhoneNumbers).size !== normalizedPhoneNumbers.length) {
      toast.warning('Please remove any duplicate phone numbers.');
      return;
    }

    if (password.trim().length < minPasswordLength) {
      toast.warning(`Please use at least ${minPasswordLength} characters for your password.`);
      return;
    }

    if (password !== passwordConfirm) {
      toast.warning('The password confirmation does not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await completeCustomerLoginMigrationPasswordSetup(
        token,
        normalizedPhoneNumbers,
        password
      );
      if (!result.ok) {
        if (result.status === 404 || result.status === 410 || result.status === 409) {
          toast.info(result.message);
          if (result.status !== 409) {
            setError(result.message);
          }
          return;
        }
        toast.error(result.message);
        return;
      }

      await finalizeLogin(
        result.data.customer_id,
        result.data.name,
        result.data.tel,
        result.data.auth_token,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updatePhoneNumberAt = (index: number, value: string) => {
    setPhoneNumbers((current) => current.map((item, itemIndex) => (
      itemIndex === index ? value : item
    )));
  };

  const addPhoneNumberField = () => {
    setPhoneNumbers((current) => [...current, '']);
  };

  const removePhoneNumberField = (index: number) => {
    setPhoneNumbers((current) => (
      current.length === 1 ? current : current.filter((_, itemIndex) => itemIndex !== index)
    ));
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-600">Loading…</div>;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-slate-950">Migration link unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#e8f5f1_0%,#f8fafc_42%,#eef5f8_100%)] px-4 py-8">
      <LoginNotificationPrompt />

      <main className="mx-auto max-w-md">
        <section className="mb-5 px-2 pt-3 pb-1">
          <div className="flex items-center justify-center gap-3">
            <div className="h-14 w-14 overflow-hidden rounded-[18px] border border-white/70 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <img src={camiyuiLogo} alt={STORE_DISPLAY_NAME} className="h-full w-full object-cover" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                {STORE_DISPLAY_NAME}
              </p>
              <h1 className="mt-1 text-[28px] font-semibold tracking-[-0.03em] text-slate-950">
                Login Setup
              </h1>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-emerald-100 bg-white/90 shadow-lg shadow-emerald-100/60 backdrop-blur">
          <div className="border-b border-emerald-100/80 bg-white px-5 py-5">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-950">
              {customerName || 'Customer'} login setup
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Add the phone number you currently use, and include any other phone numbers you also want to use for login. Then choose a password to finish your {channelLabel(channel)} setup.
            </p>
          </div>

          <div className="space-y-5 p-5">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Smartphone className="h-4 w-4 text-emerald-700" />
                Current Phone Number(s)
              </div>
              {phoneNumbers.map((phoneNumber, index) => (
                <div key={`phone-${index}`} className="flex items-center gap-3">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => updatePhoneNumberAt(index, e.target.value)}
                    placeholder={index === 0 ? 'Example: 0123456789' : 'Another phone number'}
                    className="w-full rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
                  />
                  {phoneNumbers.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removePhoneNumberField(index)}
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-red-200 hover:text-red-600"
                      aria-label={`Remove phone number ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                onClick={addPhoneNumberField}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-100"
              >
                <Plus className="h-4 w-4" />
                Add another phone number
              </button>
              <p className="text-xs leading-5 text-slate-500">
                Enter the phone number you normally use first. If you use more than one phone number, you can add the others here and use the same password with all of them.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <KeyRound className="h-4 w-4 text-emerald-700" />
                New Password
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${minPasswordLength} characters`}
                autoComplete="new-password"
                className="w-full rounded-[22px] border border-slate-200 bg-slate-50 py-4 pl-4 pr-14 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              <PasswordInput
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
                className="w-full rounded-[22px] border border-slate-200 bg-slate-50 py-4 pl-4 pr-14 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              <p className="text-xs leading-5 text-slate-500">
                Use a password you can remember. You will use the same password with any login-enabled phone number on this account.
              </p>
            </div>

            <button
              type="button"
              onClick={handleCompleteSetup}
              disabled={isSubmitting}
              className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[22px] bg-slate-950 px-4 py-4 text-base font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Finish Setup
              <ChevronRight className="h-5 w-5" />
            </button>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-sm leading-6 text-slate-600">
                This setup link is only for the customer account it was issued for. If you are helping someone else, please ask staff to generate a new link.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-6">
          <InstallPrompt mode="embedded" />
        </div>
      </main>
    </div>
  );
};

export default LoginMigrationPage;
